import React, { useEffect, useRef, useState } from 'react';
import { useFetchRequiredCredits, useGenerateAiMedia } from 'apps/t2c-fe/services';
import { useSnackbar } from 'notistack';
import { useDialog } from '../useDialog';
import { useGetUserEligibility, FeatureType } from '../useGetUserEligibility';

import { useRouter } from 'next/router';
import { useCreditContext } from '../useCreditContext';
import { AIMedia, AudioCredit } from '../../@types';
import {
  AiImageModelType,
  imageModelDetails,
  isCreditsAvailable,
  OrientationEnum,
} from '../../utils';
import { useAppContext } from '../context';
import { getRandomInt, IMAGE_DEFAULT_NAME_PREFIX } from 'apps/t2c-fe/utils';
import { useEnhancePrompt } from '../broll/useEnchancePrompt';
import { usePathname } from 'next/navigation';
import { AspectRatios } from '@t2c/ai-video-editor';
import { resizeImageFile } from 'apps/t2c-fe/components/common/ResizeFunction';
import { resolveMediaUploadId } from 'apps/t2c-fe/utils/resolveMediaUploadId';

export const useTextToImagePage = ({ refetchimages }) => {
  const [imageGenerated, setImageGenerated] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const abortController = useRef(null);
  const abortControllerSecond = useRef(null);
  const [isUploadingSecond, setIsUploadingSecond] = useState(false);
  const [openRenderGateModal, setOpenRenderGateModal] = useState(false);
  const [renderGateModalMode, setRenderGateModalMode] = useState<
    'processing' | 'upgrade' | 'noPlan'
  >('upgrade');

  const [imageGenerationData, setImageGenerationData] = useState({
    model: AiImageModelType.NANOBANANA_2,
    prompt:
      "Realistic photo of a military woman dressed in full military camo. She's strikingly beautiful, has a few freckles, dimples, smiling, brown hair, and blue eyes. She's in her late 20's. Shot close up from an iPhone 20 in portrait mode.",
    name: IMAGE_DEFAULT_NAME_PREFIX + getRandomInt(1, 1000),
    numGenerations: imageModelDetails[AiImageModelType.NANOBANANA_2].numGenerations
      ? imageModelDetails[AiImageModelType.NANOBANANA_2].numGenerations
      : 2,
    orientation: imageModelDetails[AiImageModelType.NANOBANANA_2].orientationPerModel[0],
    uploadId: '',
    secondUploadId: '',
    resolution: imageModelDetails[AiImageModelType.NANOBANANA_2].resolutionPerModel[1],
    style: imageModelDetails[AiImageModelType.NANOBANANA_2].stylesPerModel[0],
  });
  const [maxCharLimit, setMaxCharLimit] = useState<number>(
    imageModelDetails[AiImageModelType.NANOBANANA_2].maxCharLimit
  );
  const [creditsData, setCreditsData] = useState<AudioCredit | null>(null);
  const [newGenerationData, setNewGenerationData] = useState<AIMedia>();

  const supportedFormats = ['mp4', 'jpg', 'jpeg', 'png'];
  const [uploadURL, setUploadURL] = useState('');
  const [secondUploadURL, setSecondUploadURL] = useState('');
  const [isSelectedSecond, setIsSelectedSecond] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const pathName = usePathname();

  const {
    mutateAsync: mutateFetchRequiredCredits,
    isLoading: isFetchingRequiredCredits,
    data: requiredCreditsData,
    isError: isCreditFetchError,
  } = useFetchRequiredCredits();

  const { mutateAsync: getUserEligibility, isLoading: isCheckingEligibility } =
    useGetUserEligibility();

  const {
    closeDialog: closeCreditDialog,
    isDialogOpen: isCreditDialogOpen,
    openDialog: openCreditDialog,
  } = useDialog();

  const {
    closeDialog: closeProccessingModal,
    isDialogOpen: isProccessingModalOpen,
    openDialog: openProccessingModal,
  } = useDialog();

  const { mutateAsync: generateAiimage, isLoading: imageGenerationLoading } = useGenerateAiMedia();

  const { mutateAsync: mutateEnhancePrompt, isLoading: isEnhancing } = useEnhancePrompt();

  const { setUsableCredits, usableCredits } = useCreditContext();
  const { showCreditDialog } = useAppContext();

  const handleDataChange = (key: string, value: string) => {
    if (value && key === 'numGenerations') {
      setImageGenerationData((prev) => ({ ...prev, [key]: parseInt(value) }));
      return;
    }

    setImageGenerationData((prev) => ({ ...prev, [key]: value }));
  };

  const handleimageGenerate = async () => {
    let errMsg = '';
    if (!imageGenerationData.name.trim()) errMsg = 'Please Enter The Name.';
    if (!imageGenerationData.prompt.trim()) errMsg = 'Please Enter A Prompt.';
    if (imageGenerationData.numGenerations < 1) errMsg = 'Number of images should be at least 1.';
    if (imageGenerationData.numGenerations > 4)
      errMsg = 'Maximum allowed image generations are 4. Please enter lesser or equal to it.';

    if (
      imageModelDetails[imageGenerationData.model].isStartImageRequired &&
      !imageGenerationData.uploadId
    ) {
      errMsg = 'Please Select A Reference Image.';
    }
    if (errMsg) {
      enqueueSnackbar(errMsg, { variant: 'error' });
      return;
    }

    const generateMediaData = {
      imageGenerationData,
      uploadURL,
      secondUploadURL,
      type: 'image',
      url: pathName,
    };
    localStorage.setItem('generateIMedia', JSON.stringify(generateMediaData));

    const eligibility = await getUserEligibility({
      featureType: FeatureType.IMAGE_GENERATION,
      model: imageGenerationData.model,
    });
    if (!eligibility.eligible) {
      if (eligibility.isPremium) {
        if (eligibility.noPlan) {
          setOpenRenderGateModal(true);
          setRenderGateModalMode('noPlan');
          return;
        }
        setOpenRenderGateModal(true);
        setRenderGateModalMode('upgrade');
        return;
      }
      return;
    }
    if (eligibility.remainingImages < imageGenerationData.numGenerations) {
      enqueueSnackbar('Daily generation limit reached for this model. Try again later.', {
        variant: 'error',
      });
      return;
    }

    const res = await mutateFetchRequiredCredits({
      text: imageGenerationData.prompt,
      aiGenType: 'image',
      numGenerations: imageGenerationData.numGenerations,
      model: imageGenerationData.model,
    });
    setCreditsData(res);
  };

  useEffect(() => {
    if (localStorage.getItem('generateIMedia')) {
      let data;
      try {
        data = JSON.parse(localStorage.getItem('generateIMedia'));
      } catch {
        localStorage.removeItem('generateIMedia');
        return;
      }

      if (data?.complete === true) {
        setTimeout(() => {
          const storageData = data?.imageGenerationData;
          if (!imageModelDetails[storageData?.model as AiImageModelType]) {
            setImageGenerationData((prev) => ({
              ...prev,
              prompt: storageData?.prompt || prev.prompt,
              name: storageData?.name || prev.name,
            }));
            localStorage.removeItem('generateIMedia');
            enqueueSnackbar(
              'The model you selected is no longer available. Please review your settings.',
              { variant: 'info' }
            );
            return;
          }

          setImageGenerationData((prev) => ({
            ...prev,
            model: storageData?.model ?? prev.model,
            name: storageData?.name ?? prev.name,
            numGenerations: storageData?.numGenerations ?? prev.numGenerations,
            orientation: storageData?.orientation ?? prev.orientation,
            prompt: storageData?.prompt ?? prev.prompt,
            resolution: storageData?.resolution ?? prev.resolution,
            style: storageData?.style ?? prev.style,
            uploadId: storageData?.uploadId ?? prev.uploadId,
            uploadTailId: storageData?.secondUploadId ?? prev.secondUploadId,
          }));
          if (data.uploadURL) {
            setUploadURL(data.uploadURL);
          }
          if (data.secondUploadURL) {
            setSecondUploadURL(data.secondUploadURL);
          }
        }, 0);
      }
    }
  }, []);

  useEffect(() => {
    if (creditsData) {
      const isEnoughCredit = isCreditsAvailable(
        creditsData?.availableCredits,
        creditsData?.creditsInUse,
        creditsData?.requiredCredits
      );

      if (showCreditDialog) {
        openCreditDialog();
      } else {
        if (!isFetchingRequiredCredits && isEnoughCredit) {
          handleConfirmGeneration();
        } else if (!isEnoughCredit) {
          // handleUpgrade();
          openCreditDialog();
        }
      }
    }
  }, [creditsData]);

  const { push } = useRouter();

  const handleConfirmGeneration = async () => {
    try {
      setNewGenerationData(null);
      closeCreditDialog();
      const requests = Array.from({ length: imageGenerationData.numGenerations }, () =>
        generateAiimage({
          category: 'image',
          name: imageGenerationData.name.trim(),
          prompt: imageGenerationData.prompt.trim(),
          orientation: imageGenerationData.orientation,
          model: imageGenerationData.model,
          resolution: imageGenerationData.resolution,
          uploadId: imageGenerationData.uploadId,
          uploadTailId: imageGenerationData.secondUploadId,
          style: imageGenerationData?.style?.value,
        })
      );
      const responses = await Promise.all(requests);
      localStorage.removeItem('generateIMedia');
      setNewGenerationData(responses[0]);
      setUsableCredits(usableCredits - creditsData.requiredCredits);
      openProccessingModal();
      await refetchimages();
    } catch (error) {
      await refetchimages();
    }
  };

  const getImageDimensions = (file: Blob): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const preImageValidator = async (file: Blob) => {
    const dimensions = await getImageDimensions(file);

    if (dimensions.width < 300 || dimensions.height < 300) {
      throw new Error('Image must be at least 300x300 pixels.');
    }
    const aspectRatio = dimensions.width / dimensions.height;
    if (aspectRatio < 0.4 || aspectRatio > 2.5) {
      throw new Error('Aspect ratio must be between 1:2.5 and 2.5:1.');
    }
  };

  const handleImageSelect = async (url: string): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      let imageId = '';
      try {
        const pathname = new URL(url).pathname;
        const parts = pathname.split('/');
        imageId = parts[parts.length - 1];
      } catch {
        imageId = '';
      }

      try {
        const response = await fetch(url);
        let blob = await response.blob();
        const sizeMB = blob.size / (1024 * 1024);
        const maxSize = imageModelDetails[imageGenerationData.model].maxImageSize ?? 10;
        if (sizeMB >= maxSize) {
          const filePreview = URL.createObjectURL(blob);
          blob = await resizeImageFile(filePreview, {
            maxWidth: 2048,
            maxHeight: 2048,
            targetMaxBytes: maxSize * 1024 * 1024,
          });
        }

        const objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.crossOrigin = 'anonymous';

        try {
          await preImageValidator(blob);
        } catch (validationError) {
          enqueueSnackbar(validationError.message, { variant: 'error' });
          return reject(validationError);
        }

        img.onload = async () => {
          URL.revokeObjectURL(objectUrl);

          const resolvedUploadId = await resolveMediaUploadId({
            sourceUrl: url,
            blob,
            format: 'image',
            candidateId: imageId,
          });
          if (!resolvedUploadId) {
            return reject(new Error('Unable to resolve upload id for selected image.'));
          }

          if (!isSelectedSecond) {
            setImageGenerationData((prev) => ({ ...prev, uploadId: resolvedUploadId }));
            setUploadURL(url);
          } else {
            setImageGenerationData((prev) => ({ ...prev, secondUploadId: resolvedUploadId }));
            setSecondUploadURL(url);
          }
          resolve();
        };

        img.onerror = () => reject(new Error('Failed to load image.'));
        img.src = objectUrl;
      } catch (err) {
        reject(err);
      }
    });
  };

  const handleUpdateImage = (uploadId: string, url: string) => {
    setImageGenerationData((prev) => ({ ...prev, uploadId }));
    setUploadURL(url);
  };

  const handleUpdateSecondImage = (uploadId: string, url: string) => {
    setImageGenerationData((prev) => ({ ...prev, secondUploadId: uploadId }));
    setSecondUploadURL(url);
  };

  const handlePromptEnhance = async () => {
    try {
      const res = await mutateEnhancePrompt({
        prompt: imageGenerationData.prompt,
        model: 'image',
      });
      handleDataChange('prompt', res);
    } catch (error) {
      enqueueSnackbar('Failed to enchance prompt', { variant: 'error' });
    }
  };

  const applyModelDefaults = (model: AiImageModelType) => {
    const preferredIndex = model === AiImageModelType.NANOBANANA_2 ? 1 : 0;

    const config = imageModelDetails[model];

    if (imageGenerationData.model) {
      setImageGenerationData((prev) => ({
        ...prev,
        model,

        numGenerations: model === AiImageModelType.NANOBANANA_2 ? 1 : 2,

        orientation: config.orientationPerModel[0] ?? AspectRatios['16_9'],

        resolution: config.resolutionPerModel[preferredIndex] ?? config.resolutionPerModel[0] ?? '',

        style: config.stylesPerModel[preferredIndex] ??
          config.stylesPerModel[0] ?? {
            label: '',
            value: '',
          },

        uploadId: '',

        secondUploadId: '',
      }));

      const maxchar = config.maxCharLimit;

      setMaxCharLimit(maxchar);
      if (imageGenerationData.prompt.length > maxchar) {
        handleDataChange('prompt', imageGenerationData.prompt.slice(0, maxchar));
      }
    }
    setUploadURL('');
    setSecondUploadURL('');
    localStorage.removeItem('generateIMedia');

    if (abortController.current) {
      abortController.current.abort();
      setIsUploading(false);
    }
    if (abortControllerSecond.current) {
      abortControllerSecond.current.abort();
      setIsUploadingSecond(false);
    }
  };

  const handleUpgrade = () => push('/subscription');
  return {
    imageGenerated,
    setImageGenerated,
    supportedFormats,
    imageGenerationData,
    setImageGenerationData,
    maxCharLimit,
    setMaxCharLimit,
    handleDataChange,
    handleimageGenerate,
    isCreditDialogOpen,
    closeCreditDialog,
    isFetchingRequiredCredits,
    requiredCreditsData,
    handleConfirmGeneration,
    imageGenerationLoading,
    isProccessingModalOpen,
    closeProccessingModal,
    isCheckingEligibility,
    handleUpgrade,
    uploadURL,
    setUploadURL,
    secondUploadURL,
    setSecondUploadURL,
    setIsSelectedSecond,
    handleImageSelect,
    handleUpdateImage,
    handleUpdateSecondImage,
    isUploading,
    applyModelDefaults,
    abortController,
    abortControllerSecond,
    setIsUploading,
    isUploadingSecond,
    setIsUploadingSecond,
    openRenderGateModal,
    renderGateModalMode,
    setOpenRenderGateModal,
    setRenderGateModalMode,
    isEnhancing,
    handlePromptEnhance,
    newGenerationData,
    preImageValidator,
  };
};
